import { Component, OnInit, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { NgForm, UntypedFormBuilder } from '@angular/forms'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { NgbDatepicker, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import { Subscription } from 'rxjs'

import { SiteService } from '../../../../services/site.service'
import { MessageService } from '../../../../services/message.service'
import { TranslateService } from '@ngx-translate/core'
import { CustomerRequestsService } from '../../../../services/customer-requests.service'
import { DatepickerService } from '../../../../services/datepicker.service'

import { AddressChangeViewModel } from '../../../../models/addressChangeViewModel'
import { ErrorMessageModalComponent } from '../../../modals/error-message-modal/error-message-modal.component'

@Component({
    selector: 'app-request-address-change-detail',
    templateUrl: './request-address-change-detail.component.html',
    styleUrls: ['./request-address-change-detail.component.scss'],
    standalone: false
})
export class RequestAddressChangeDetailComponent implements OnInit 
{
  @ViewChild('customForm') public customForm!: NgForm
  @ViewChild('effectiveDate') effectiveDate!: NgbDatepicker
  @ViewChild('e') datepicker!: NgbDatepicker

  formSubmitted: boolean = false
  invalidInsurancePolicyFile: boolean = false
  invalidOwnershipFile: boolean = false
  invalidEffectiveDateFormat: boolean = false
  invalidEffectiveDate: boolean = false
  isActiveLease: boolean = true
  outOfProvinceIsRequired: boolean = false
  insurancePolicyIsRequired: boolean = false

  postalCodePattern = "^[A-Z][0-9][A-Z][0-9][A-Z][0-9]$"
  request =
  {
    address1: '',
    address2: '',
    city: '',
    province: '',
    postalCode: '',
    effectiveDate: new Date()
  }
  insurancePolicyFile: any = undefined
  ownershipFile: any = undefined
  referenceNumber: string = ''
  addressChangeViewModel = new AddressChangeViewModel()
  requestEffectiveDate: NgbDateStruct = 
  {
    year:  new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    day:   new Date().getDate()
  }
  provinces: any[]
  thisYear   = new Date().getFullYear()
  nextYear   = this.thisYear + 1
  currentMonth = new Date().getMonth() + 1

  preferredLanguageChangedSubscription: Subscription

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private siteService: SiteService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private customerRequestsService: CustomerRequestsService,
    public i18n: DatepickerService
  ) 
  { 
      this.provinces = [
        { provinceCode: 'ON', name: 'Ontario' },
        { provinceCode: 'QC', name: 'Quebec' },
        { provinceCode: 'NS', name: 'Nova Scotia' },
        { provinceCode: 'NB', name: 'New Brunswick' },
        { provinceCode: 'MB', name: 'Manitoba' },
        { provinceCode: 'BC', name: 'British Columbia' },
        { provinceCode: 'PE', name: 'Prince Edward Island' },
        { provinceCode: 'SK', name: 'Saskatchewan' },
        { provinceCode: 'AB', name: 'Alberta' },
        { provinceCode: 'NL', name: 'Newfoundland & Labrador' },
        { provinceCode: 'NT', name: 'Northwest Territories' },
        { provinceCode: 'YT', name: 'Yukon' },
        { provinceCode: 'NU', name: 'Nunavut' }
      ]

      this.preferredLanguageChangedSubscription = this.messageService.preferredLanguageChanged$.subscribe(() =>
      {
        this.translateService.currentLang === 'fr' ? this.i18n.setLang('fr') : this.i18n.setLang('en')
		  })
  }

  ngOnInit() 
  {
      this.translateService.currentLang === 'fr' ? this.i18n.setLang('fr') : this.i18n.setLang('en')

      this.retrieveAddressAccountChangeViewModel()
  }

  retrieveAddressAccountChangeViewModel()
  {
      this.messageService.startedHttpRequest('retrieveAddressAccountChangeViewModel')
      this.customerRequestsService
        .getAddressChangeRequestViewModel()
        .subscribe({
          next: (apiResponse) =>
          {
            this.addressChangeViewModel = apiResponse
            
            if(this.addressChangeViewModel && this.addressChangeViewModel.isActiveLease)
            {
                this.request.address1 = this.addressChangeViewModel.address1
                this.request.address2 = this.addressChangeViewModel.address2
                this.request.city = this.addressChangeViewModel.city
                this.request.province = this.addressChangeViewModel.province
                this.request.postalCode = this.addressChangeViewModel.postalCode
            }
            else
            {
              this.isActiveLease = false
            }
          },
          error: (error) =>
          {
            this.isActiveLease = false
            this.messageService.completedHttpRequest('retrieveAddressAccountChangeViewModel')
  
            const dialogConfig = new MatDialogConfig()
            dialogConfig.width = '500px',
            dialogConfig.data = {
              title: this.translateService.instant('COMMON_LBL_ERROR'),
              message: this.translateService.instant('COMMON_LBL_ERROR_MSG'),
            }
            
            this.dialog.open(ErrorMessageModalComponent, dialogConfig)
          },
          complete: () =>
          {
            this.messageService.completedHttpRequest('retrieveAddressAccountChangeViewModel')
          }
        })
  }

  handleInsurancePolicyFileInput(files: FileList) 
  {
    this.invalidInsurancePolicyFile = false
    this.insurancePolicyFile = files.item(0);

    if(this.insurancePolicyFile)
    {
      const allowedExtensions = ['pdf','bmp','jpeg','jpg', 'png', 'gif']
      const fileExtension = this.insurancePolicyFile.name.split('.').pop().toLowerCase()
  
      if (!allowedExtensions.includes(fileExtension)) 
      {
        this.invalidInsurancePolicyFile = true
      }
    }
  }

  handleOwnershipFileInput(files: FileList) 
  {
    this.invalidOwnershipFile = false
    this.ownershipFile = files.item(0);

    if(this.ownershipFile)
    {
      const allowedExtensions = ['pdf','bmp','jpeg','jpg', 'png', 'gif'];
      const fileExtension = this.insurancePolicyFile.name.split('.').pop().toLowerCase()
  
      if (!allowedExtensions.includes(fileExtension)) 
      {
        this.invalidOwnershipFile = true
      }
    }
  }

  handleProvinceSelected()
  {
    this.ownershipFile = undefined
    this.insurancePolicyFile = undefined

    this.outOfProvinceIsRequired = (this.request.province !== this.addressChangeViewModel.province)
    this.insurancePolicyIsRequired = (this.request.province !== this.addressChangeViewModel.province) && 
                                (this.request.province !== 'MB' && this.request.province !== 'SK' && this.request.province !== 'BC')

    // if(this.outOfProvinceIsRequired)
    // {
    //   const uploadOwnershipBtn = document.getElementById('ownershipFileBtn')
    //   const ownershipFileInput = document.getElementById('ownershipFileInput')

    //   uploadOwnershipBtn.addEventListener('click', () => {
    //     ownershipFileInput.click()
    //   })
    // }

    if(this.insurancePolicyIsRequired)
    {
      const uploadInsurancePolicyFileBtn = document.getElementById('insurancePolicyFileBtn')
      const insurancePolicyFileInput = document.getElementById('insurancePolicyFileInput')

      if (uploadInsurancePolicyFileBtn && insurancePolicyFileInput) 
      {
        uploadInsurancePolicyFileBtn.addEventListener('click', () => {
          insurancePolicyFileInput.click()
        })
      }
    }
  }
  
  submitForm(customerForm: NgForm)
  {
    this.formSubmitted = true

		this.validateEffectiveDate()

    if(this.outOfProvinceIsRequired)
    {
      this.validateFiles()
    }

    if (customerForm.invalid || this.invalidEffectiveDate || this.invalidEffectiveDateFormat || 
        this.invalidInsurancePolicyFile || this.invalidOwnershipFile)
    {
      return
    }

    if(this.request.address1==this.addressChangeViewModel.address1 && 
      this.request.address2==this.addressChangeViewModel.address2 && 
      this.request.city==this.addressChangeViewModel.city && 
      this.request.province==this.addressChangeViewModel.province && 
      this.request.postalCode==this.addressChangeViewModel.postalCode)
    {
        const dialogConfig = new MatDialogConfig()
        dialogConfig.width = '500px',
        dialogConfig.data = {
            title: '',
            message: this.translateService.instant('REQUEST_ADDRESS_CHANGE_ERR_SAME_INFO'),
        }		
        this.dialog.open(ErrorMessageModalComponent, dialogConfig)

        return
    }

    this.request.effectiveDate = new Date(this.requestEffectiveDate.year, this.requestEffectiveDate.month - 1, this.requestEffectiveDate.day)

    const requestSummaryList: { key: string; value: string }[] = [
      { key: this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_NAME'), value: this.siteService.customerAccount?.fullName ?? '' },
      { key: this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_LEASENUM'), value: this.addressChangeViewModel.leaseNumber },
      { key: this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_ADDRESS1'), value: this.request.address1 },
      { key: this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_ADDRESS2'), value: this.request.address2 },
      { key: this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_CITY'), value: this.request.city },
      { key: this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_PROVINCE'), value: this.request.province },
      { key: this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_POSTAL_CODE'), value: this.request.postalCode },
      { key: this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_EFFECTIVE_DATE'), value: this.requestEffectiveDate.year + '/' + this.requestEffectiveDate.month + '/' + this.requestEffectiveDate.day}
  
    ]

    return this.customerRequestsService.requestConfirmation(this.translateService.instant('REQUEST_ADDRESS_CHANGE_CONFIRM_PRINT_TITLE'), requestSummaryList)
    .then((result) =>
    {
      if(result)
      {
        let formData = new FormData()
        formData.append('address1', this.request.address1)
        formData.append('address2', this.request.address2)
        formData.append('city', this.request.city)
        formData.append('province', this.request.province)
        formData.append('postalCode', this.request.postalCode)
        formData.append('insurancePolicyFile', this.insurancePolicyFile)
        formData.append('ownershipFile', this.ownershipFile)
        formData.append('effectiveDate', this.requestEffectiveDate.year + '-' + this.requestEffectiveDate.month + '-' + this.requestEffectiveDate.day)

        this.messageService.startedHttpRequest('submitForm')
        this.customerRequestsService
          .addAddressChangeRequest(formData)
          .subscribe({
            next: (apiResponse) =>
            {
              this.referenceNumber = apiResponse

              if ((this.referenceNumber != undefined) && (this.referenceNumber != ''))
              {
                this.customerRequestsService.requestSummary(this.referenceNumber, this.translateService.instant('REQUEST_ADDRESS_CHANGE_HEADING'), requestSummaryList)
            
                this.router.navigate(['/request-list'])
              }	
                            
              this.formSubmitted = false
            },
            error: (error) =>
            {
              this.messageService.completedHttpRequest('submitForm')
        
              const dialogConfig = new MatDialogConfig()
              dialogConfig.width = '500px',
              dialogConfig.data = {
                title: this.translateService.instant('COMMON_LBL_ERROR'),
                message: this.translateService.instant('COMMON_LBL_ERROR_MSG')
              }		
              this.dialog.open(ErrorMessageModalComponent, dialogConfig)
            },
            complete: () =>
            {
              this.messageService.completedHttpRequest('submitForm')
            }
          })
      }
    })	
  }

  validateFiles()
  {
    this.invalidInsurancePolicyFile = false
    //this.invalidOwnershipFile = false
    const allowedExtensions = ['pdf','bmp','jpeg','jpg', 'png', 'gif']
    let fileExtension = ''

    // if(this.ownershipFile == undefined)
    // {
    //   this.invalidOwnershipFile = true
    // }
    // else
    // {
    //   fileExtension = this.ownershipFile.name.split('.').pop().toLowerCase()

    //   if (!allowedExtensions.includes(fileExtension)) 
    //   {
    //     this.invalidOwnershipFile = true
    //   }
    // }

    if(this.insurancePolicyIsRequired)
    {
      if(this.insurancePolicyFile == undefined)
      {
        this.invalidInsurancePolicyFile = true
      }
      else
      {
        fileExtension = this.insurancePolicyFile.name.split('.').pop().toLowerCase()
  
        if (!allowedExtensions.includes(fileExtension)) 
        {
          this.invalidInsurancePolicyFile = true
        }
      }
    }
  }

  validateEffectiveDate()
  {
    this.invalidEffectiveDateFormat = false
    this.invalidEffectiveDate = false

    if(!this.requestEffectiveDate.year || !this.requestEffectiveDate.month || !this.requestEffectiveDate.day)
    {
      this.invalidEffectiveDateFormat = true
    }
    else
    {
      const selectedDate = new Date(this.requestEffectiveDate.year, this.requestEffectiveDate.month - 1, this.requestEffectiveDate.day)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if(selectedDate < today)
      {
        this.invalidEffectiveDate = true
      }
    }
  }

  postalCodeValidator(event: any) 
  {
    const pattern = /^[a-zA-Z0-9]*$/  
    if (!pattern.test(event.target.value)) 
    {
      event.target.value = event.target.value.replace(/[^a-zA-Z0-9]/g, "")
      this.request.postalCode = event.target.value
    }
    this.request.postalCode = this.request.postalCode.toUpperCase()
  }

  effectiveDateValidator(event: any) 
  {
    this.invalidEffectiveDateFormat = false
    this.invalidEffectiveDate = false

    const pattern = /^[0-9\-]*$/
    if (!pattern.test(event.target.value)) 
    {
      event.target.value = event.target.value.replace(/[^0-9\-]/g, "")
      this.requestEffectiveDate = event.target.value
    }

    const datePattern = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    this.invalidEffectiveDateFormat = !datePattern.test(event.target.value)
  }

}
